import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Trash2, Upload, Eye, X, ChevronLeft, ChevronRight, Edit, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatDate } from '@/utils/dateFormat';
import { useCurrency } from '@/contexts/CurrencyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const Deposits = () => {
  const [entries, setEntries] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [viewReceiptOpen, setViewReceiptOpen] = useState(false);
  const [viewingReceipt, setViewingReceipt] = useState(null);
  const [editingLine, setEditingLine] = useState(null);
  const { currencySymbol } = useCurrency();

  // Initialize 10 empty entries for the day
  const initializeEntries = (existingEntries = []) => {
    const newEntries = [];
    for (let i = 1; i <= 10; i++) {
      const existing = existingEntries.find(e => e.line_number === i);
      if (existing) {
        newEntries.push(existing);
      } else {
        newEntries.push({
          id: null,
          date: selectedDate,
          entry_type: 'deposit',
          line_number: i,
          access_bank: { amount: 0, description: '', receipt_url: null, fund_movement: '' },
          zenith_bank: { amount: 0, description: '', receipt_url: null, fund_movement: '' },
          others: { amount: 0, description: '', receipt_url: null, fund_movement: '' }
        });
      }
    }
    return newEntries;
  };

  useEffect(() => {
    fetchEntries();
  }, [selectedDate]);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reconciliation/deposit?date=${selectedDate}`, getAuthHeader());
      setEntries(initializeEntries(response.data));
    } catch (error) {
      toast.error('Failed to fetch entries');
      setEntries(initializeEntries([]));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (lineNumber, bank, field, value) => {
    setEntries(prev => prev.map(entry => {
      if (entry.line_number === lineNumber) {
        return {
          ...entry,
          [bank]: {
            ...entry[bank],
            [field]: field === 'amount' ? parseFloat(value) || 0 : value
          }
        };
      }
      return entry;
    }));
  };

  const saveEntry = async (entry) => {
    // Check if entry has any data
    const hasData = 
      (entry.access_bank?.amount > 0 || entry.access_bank?.description || entry.access_bank?.fund_movement) ||
      (entry.zenith_bank?.amount > 0 || entry.zenith_bank?.description || entry.zenith_bank?.fund_movement) ||
      (entry.others?.amount > 0 || entry.others?.description || entry.others?.fund_movement);

    if (!hasData) return;

    setSaving(true);
    try {
      if (entry.id) {
        await axios.put(`${API}/reconciliation/${entry.id}`, entry, getAuthHeader());
      } else {
        const response = await axios.post(`${API}/reconciliation`, entry, getAuthHeader());
        setEntries(prev => prev.map(e => 
          e.line_number === entry.line_number ? { ...e, id: response.data.id } : e
        ));
      }
      toast.success('Entry saved');
      setEditingLine(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const saveAllEntries = async () => {
    setSaving(true);
    let savedCount = 0;
    
    for (const entry of entries) {
      const hasData = 
        (entry.access_bank?.amount > 0 || entry.access_bank?.description || entry.access_bank?.fund_movement) ||
        (entry.zenith_bank?.amount > 0 || entry.zenith_bank?.description || entry.zenith_bank?.fund_movement) ||
        (entry.others?.amount > 0 || entry.others?.description || entry.others?.fund_movement);

      if (hasData) {
        try {
          if (entry.id) {
            await axios.put(`${API}/reconciliation/${entry.id}`, entry, getAuthHeader());
          } else {
            const response = await axios.post(`${API}/reconciliation`, entry, getAuthHeader());
            setEntries(prev => prev.map(e => 
              e.line_number === entry.line_number ? { ...e, id: response.data.id } : e
            ));
          }
          savedCount++;
        } catch (error) {
          console.error(`Failed to save line ${entry.line_number}:`, error);
        }
      }
    }
    
    setSaving(false);
    if (savedCount > 0) {
      toast.success(`Saved ${savedCount} entries`);
      fetchEntries();
    }
  };

  const deleteEntry = async (entry) => {
    if (!entry.id) return;
    if (!window.confirm('Delete this entry?')) return;
    
    try {
      await axios.delete(`${API}/reconciliation/${entry.id}`, getAuthHeader());
      toast.success('Entry deleted');
      fetchEntries();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const handleReceiptUpload = async (entryId, bank, file) => {
    if (!entryId) {
      toast.error('Please save the entry first before uploading a receipt');
      return;
    }
    
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      toast.error('Please upload an image or PDF file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        await axios.post(`${API}/reconciliation/${entryId}/receipt/${bank}`, { receipt: reader.result }, getAuthHeader());
        toast.success('Receipt uploaded');
        fetchEntries();
      } catch (error) {
        toast.error('Failed to upload receipt');
      }
    };
    reader.readAsDataURL(file);
  };

  const deleteReceipt = async (entryId, bank) => {
    if (!window.confirm('Delete this receipt?')) return;
    try {
      await axios.post(`${API}/reconciliation/${entryId}/receipt/${bank}`, { receipt: null }, getAuthHeader());
      toast.success('Receipt deleted');
      fetchEntries();
    } catch (error) {
      toast.error('Failed to delete receipt');
    }
  };

  const handleViewReceipt = (receiptUrl) => {
    setViewingReceipt(receiptUrl);
    setViewReceiptOpen(true);
  };

  const changeDate = (days) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + days);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const calculateBankTotal = (bank) => {
    return entries.reduce((sum, entry) => sum + (entry[bank]?.amount || 0), 0);
  };

  const calculateGrandTotal = () => {
    return calculateBankTotal('access_bank') + calculateBankTotal('zenith_bank') + calculateBankTotal('others');
  };

  const isEditing = (lineNumber) => editingLine === lineNumber;

  const renderBankCell = (entry, bank) => {
    const bankData = entry[bank] || { amount: 0, description: '', receipt_url: null, fund_movement: '' };
    const editing = isEditing(entry.line_number);
    
    return (
      <>
        <td className="p-1 border-r">
          <Input
            type="number"
            value={bankData.amount || ''}
            onChange={(e) => handleInputChange(entry.line_number, bank, 'amount', e.target.value)}
            className="w-20 h-7 text-xs text-right"
            placeholder="0.00"
            disabled={!editing && entry.id}
          />
        </td>
        <td className="p-1 border-r">
          <Input
            value={bankData.description || ''}
            onChange={(e) => handleInputChange(entry.line_number, bank, 'description', e.target.value)}
            className="w-24 h-7 text-xs"
            placeholder="Desc..."
            disabled={!editing && entry.id}
          />
        </td>
        <td className="p-1 border-r">
          {bankData.receipt_url ? (
            <div className="flex items-center justify-center gap-1">
              <button
                onClick={() => handleViewReceipt(bankData.receipt_url)}
                className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                title="View Receipt"
              >
                <Eye className="w-3 h-3" />
              </button>
              <button
                onClick={() => deleteReceipt(entry.id, bank)}
                className="p-1 text-red-600 hover:bg-red-50 rounded"
                title="Delete Receipt"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <label className="cursor-pointer flex justify-center">
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => {
                  if (e.target.files[0]) {
                    handleReceiptUpload(entry.id, bank, e.target.files[0]);
                  }
                  e.target.value = '';
                }}
                className="hidden"
              />
              <span className="p-1 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded">
                <Upload className="w-3 h-3" />
              </span>
            </label>
          )}
        </td>
        <td className="p-1 border-r">
          <Input
            value={bankData.fund_movement || ''}
            onChange={(e) => handleInputChange(entry.line_number, bank, 'fund_movement', e.target.value)}
            className="w-20 h-7 text-xs"
            placeholder="Fund Mvt"
            disabled={!editing && entry.id}
          />
        </td>
      </>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Deposits</h1>
          <p className="text-slate-600">Daily bank deposit reconciliation</p>
        </div>
        <Button 
          onClick={saveAllEntries} 
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save All'}
        </Button>
      </div>

      {/* Date Navigation */}
      <div className="card p-4">
        <div className="flex items-center justify-center gap-4">
          <Button variant="outline" size="sm" onClick={() => changeDate(-1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-40"
            />
            <span className="text-sm text-slate-600 font-medium">
              {formatDate(selectedDate)}
            </span>
          </div>
          <Button variant="outline" size="sm" onClick={() => changeDate(1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
          >
            Today
          </Button>
        </div>
      </div>

      {/* Entry Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-100">
                <th rowSpan={2} className="p-2 border-r text-left w-8">#</th>
                <th colSpan={4} className="p-2 border-r text-center bg-blue-50">Access Bank</th>
                <th colSpan={4} className="p-2 border-r text-center bg-purple-50">Zenith Bank</th>
                <th colSpan={4} className="p-2 border-r text-center bg-amber-50">Others</th>
                <th rowSpan={2} className="p-2 text-center w-24">Actions</th>
              </tr>
              <tr className="bg-slate-50 text-xs">
                <th className="p-1 border-r bg-blue-50">Amount</th>
                <th className="p-1 border-r bg-blue-50">Description</th>
                <th className="p-1 border-r bg-blue-50">Receipt</th>
                <th className="p-1 border-r bg-blue-50">Fund Mvt</th>
                <th className="p-1 border-r bg-purple-50">Amount</th>
                <th className="p-1 border-r bg-purple-50">Description</th>
                <th className="p-1 border-r bg-purple-50">Receipt</th>
                <th className="p-1 border-r bg-purple-50">Fund Mvt</th>
                <th className="p-1 border-r bg-amber-50">Amount</th>
                <th className="p-1 border-r bg-amber-50">Description</th>
                <th className="p-1 border-r bg-amber-50">Receipt</th>
                <th className="p-1 border-r bg-amber-50">Fund Mvt</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={14} className="text-center py-8 text-slate-500">Loading...</td>
                </tr>
              ) : (
                entries.map((entry) => (
                  <tr key={entry.line_number} className="border-t hover:bg-slate-50">
                    <td className="p-1 border-r font-medium text-slate-500 text-center">{entry.line_number}</td>
                    {renderBankCell(entry, 'access_bank')}
                    {renderBankCell(entry, 'zenith_bank')}
                    {renderBankCell(entry, 'others')}
                    <td className="p-1 text-center">
                      <div className="flex justify-center gap-1">
                        {entry.id ? (
                          <>
                            {isEditing(entry.line_number) ? (
                              <button
                                onClick={() => saveEntry(entry)}
                                className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                                title="Save"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => setEditingLine(entry.line_number)}
                                className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                title="Edit"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => deleteEntry(entry)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => saveEntry(entry)}
                            className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                            title="Save"
                          >
                            <Save className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr className="bg-slate-100 font-semibold text-xs">
                <td className="p-2 border-r">Total</td>
                <td className="p-2 border-r text-right bg-blue-50">{currencySymbol}{calculateBankTotal('access_bank').toLocaleString()}</td>
                <td className="p-2 border-r bg-blue-50" colSpan={3}></td>
                <td className="p-2 border-r text-right bg-purple-50">{currencySymbol}{calculateBankTotal('zenith_bank').toLocaleString()}</td>
                <td className="p-2 border-r bg-purple-50" colSpan={3}></td>
                <td className="p-2 border-r text-right bg-amber-50">{currencySymbol}{calculateBankTotal('others').toLocaleString()}</td>
                <td className="p-2 border-r bg-amber-50" colSpan={3}></td>
                <td className="p-2 text-right text-emerald-600">
                  Total: {currencySymbol}{calculateGrandTotal().toLocaleString()}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* View Receipt Dialog */}
      <Dialog open={viewReceiptOpen} onOpenChange={setViewReceiptOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Receipt</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-4">
            {viewingReceipt && (
              viewingReceipt.startsWith('data:application/pdf') ? (
                <div className="w-full">
                  <iframe
                    src={viewingReceipt}
                    className="w-full h-[500px] border rounded-lg"
                    title="Receipt PDF"
                  />
                </div>
              ) : (
                <img
                  src={viewingReceipt}
                  alt="Receipt"
                  className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg"
                />
              )
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Deposits;
